<template>
  <base-page-layout
    v-if="!hideScreen"
    title="Resultados gerais de campanhas"
    :max-filter-days="93"
    set-campaigns-date-range
    default-date="ultimosTrintaDias"
    :filters="['data', 'campanhas', 'produtos', 'sem-produtos', 'canais']"
    :campaigns-filters-status="['FINISHED']"
    use-campaign-frequency
    use-filter-button
  >
    <v-row class="pt-2">
      <v-col v-for="(card, cardIndex) in cardsMetrics" :key="cardIndex" cols="6" sm="4" md="3">
        <cbm-card
          :number-text="card.value"
          :title="card.text"
          :tooltip="card.helpText"
          :boolean-loader="card.loading"
          :is-currency="card.isCurrency"
          :percent-text="card.underText"
          :percent-description="card.description"
          :quick-action="card.quickAction"
          :unset-percentage-colors="!card.usePercentageColor"
        />
      </v-col>
    </v-row>
    <v-row class="pt-2 pl-1">
      <v-col cols="12">
        <revenue-history :campaign-ids="getSelectedCampaignsIds" />
      </v-col>
    </v-row>

    <v-row class="pl-1">
      <v-col cols="5" class="py-0">
        <not-received-campaigns-table :campaign-ids="getSelectedCampaignsIds" />
      </v-col>
      <v-col cols="7" class="py-0">
        <not-received-campaigns-cards :campaign-ids="getSelectedCampaignsIds" />
      </v-col>
    </v-row>

    <v-row class="pt-2">
      <v-col cols="12">
        <channels-table :campaign-ids="getSelectedCampaignsIds" :product-ids="stateFilters.products" :channels="mode" />
      </v-col>
    </v-row>

    <v-row class="pt-2">
      <v-col cols="12">
        <buy-profile-funnel :campaign-ids="getSelectedCampaignsIds" :product-ids="stateFilters.products" :channels="mode" />
      </v-col>
    </v-row>

    <campaign-history-table
      :query-variables="queryVariables"
      :channels="channels"
      :get-selected-campaigns-ids="getSelectedCampaignsIds"
      :skip-queries="skipQueries"
    />
  </base-page-layout>

  <div v-else>
    <error-page-layout
      error-type=""
      color-type="error"
      title="Em manutenção"
      first-message="Desculpe pelo transtorno! Estamos fazendo melhorias na nossa plataforma para oferecer a você uma experiência ainda mais incrível. Voltaremos em breve, agradecemos pela sua compreensão e paciência."
      image-name="expired.png"
    >
    </error-page-layout>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import BasePageLayout from '@/components/BasePageLayout.vue'
import CbmCard from '@/components/CbmCard.vue'
import { QUERY_GET_AGGREGATED_CAMPAIGNS_RESULTS_METRICS, QUERY_GET_CAMPAIGNS_IDS, QUERY_METRICS_KPI } from '@/modules/campanhas/resultados-gerais/graphql'
import { assertArray, localeString } from '@/config/helpers.js'
import { deepDelete } from '@/helpers/deepDelete'
import { DateTime } from 'luxon'
import {
  criteriaGroup,
  campaignReceivedCriteria,
  campaignReceivedAndPurchasedCriteria,
  campaignReceivedAndPurchasedAnyCampaignPeriodCriteria,
  campaignReceivedAndPurchasedAnyProductCriteria,
  campaignReceivedAndOnlyPurchasedAnyProductCriteria,
} from '@/components/quick-actions/segmentationGroups.js'

import ErrorPageLayout from '@/components/ErrorPageLayout.vue'
import CampaignHistoryTable from './components/CampaignHistoryTable.vue'

export default {
  name: 'GeneralCampaignResults',
  components: {
    BasePageLayout,
    ErrorPageLayout,
    CbmCard,
    CampaignHistoryTable,
    NotReceivedCampaignsTable: () => import('@/modules/campanhas/resultados-gerais/pages/views/components/NotReceivedCampaignsTable.vue'),
    NotReceivedCampaignsCards: () => import('@/modules/campanhas/resultados-gerais/pages/views/components/NotReceivedCampaignsCards.vue'),
    RevenueHistory: () => import('@/modules/campanhas/resultados-gerais/pages/views/components/RevenueHistory.vue'),
    ChannelsTable: () => import('@/modules/campanhas/resultados-gerais/pages/views/components/ChannelsTable.vue'),
    BuyProfileFunnel: () => import('@/modules/campanhas/resultados-gerais/pages/views/components/BuyProfileFunnel.vue'),
  },

  data() {
    return {
      metrics: {},
      metricsKpi: {},
      selectedCampaignsIds: [],
      aggregatedCampaignMetrics: {},
      loadingAggregatedCampaignMetrics: true,
    }
  },
  computed: {
    hideScreen() {
      return false
    },
    ...mapState({
      stateFilters: (state) => state.filters,
      dateFilterSelectedPeriod: (state) => state.dateFilterSelectedPeriod,
    }),

    skipQueries() {
      return this.hideScreen || this.notExistDateInFilter || !this.channels.length || !this.mode.length
    },

    queryVariables() {
      const { data, noProducts, products } = this.stateFilters
      const [startDate, endDate] = assertArray(data)

      const variables = {
        measuringDate: [],
        noProducts,
        status: ['FINISHED'],
        frequencyType: 'ONETIME',
        campaignTypes: ['SINGLE_CHANNEL', 'OMNI_CHANNEL'],
      }

      if (startDate && endDate) variables.measuringDate = [startDate, endDate]
      if (assertArray(products).length) variables.products = products

      return variables
    },
    mode() {
      const { channels } = this.stateFilters
      if (!channels) return []
      return channels.map((channel) => channel.toUpperCase())
    },
    channels() {
      const { channels } = this.stateFilters
      if (!channels) return []
      return channels.map((channel) => channel.toLowerCase())
    },

    notExistDateInFilter() {
      return !this.stateFilters?.data?.[0]
    },

    cardsMetrics() {
      const { value, description } = this.getAverageTicketText(
        this.aggregatedCampaignMetrics?.average_ticket_clients_received,
        this.metricsKpi?.average_ticket?.value,
        1
      )

      return [
        {
          text: 'Clientes que compraram',
          value: localeString(this.aggregatedCampaignMetrics?.clients_received_bought || 0),
          helpText: 'Total de clientes únicos que receberam alguma campanha no período selecionado no filtro.',
          isInteger: true,
          loading: this.loadingAggregatedCampaignMetrics || this.notExistDateInFilter,
          quickAction: {
            id: 'received_and_purchased_any_campaign',
            shortDescription: `Clientes que haviam recebido campanhas e compraram ${this.quickActionPeriodDescriptionClientsReceivedBought}`,
            longDescription: `Clientes que haviam recebido campanhas e compraram ${this.quickActionPeriodDescriptionClientsReceivedBought}`,
            segmentationGroup: criteriaGroup([campaignReceivedAndPurchasedAnyCampaignPeriodCriteria(this.getSelectedCampaignsIds)]),
          },
        },
        {
          text: 'Ticket médio',
          value: localeString(this.aggregatedCampaignMetrics?.average_ticket_clients_received || 0, 2),
          helpText:
            'Ticket médio dos clientes que compraram no período de mensuração, após receber um disparo de algumas das campanhas do período,  em comparação com o ticket médio total, clube e não clube, no mesmo período.',
          underText: localeString(value || 0),
          description,
          isCurrency: true,
          loading: this.loadingAggregatedCampaignMetrics || this.notExistDateInFilter,
        },
        {
          text: 'Faturamento',
          value: localeString(this.aggregatedCampaignMetrics?.campaign_revenue || 0, 0),
          helpText: 'Faturamento dos clientes que receberam e compraram em comparação com o faturamento total, clube e não clube, no mesmo período.',
          underText: `${this.getRateMetrics(this.aggregatedCampaignMetrics?.campaign_revenue || 0, this.metricsKpi?.revenue?.value || 0, 1)}`,
          description: 'do faturamento total',
          isCurrency: true,
          loading: this.loadingAggregatedCampaignMetrics || this.notExistDateInFilter,
        },
        {
          text: 'Alavancagem',
          value: this.checkIfSomeCampaignModesHasCosts(this.queryVariables?.mode) ? this.localeString(this.aggregatedCampaignMetrics?.total_roi || 0, 2) : '-',
          helpText: 'Faturamento gerado para cada real investido na campanha.',
          underText: this.checkIfSomeCampaignModesHasCosts(this.queryVariables?.mode)
            ? `R$ ${localeString(this.aggregatedCampaignMetrics?.total_price || 0, 0)}`
            : null,
          isInteger: true,
          description: this.checkIfSomeCampaignModesHasCosts(this.queryVariables?.mode)
            ? 'investimento da campanha'
            : 'Os canais selecionados não possuem custo unitário',
          loading: this.loadingAggregatedCampaignMetrics || this.notExistDateInFilter,
        },
      ]
    },

    quickActionPeriodDescriptionClientsReceivedBought() {
      const [initialDate = '', finalDate = ''] = this.queryVariables?.measuringDate
      const formattedInitialDate = DateTime.fromFormat(initialDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
      const formattedFinalDate = DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')

      const periodDescriptions = {
        diaMaisRecente: 'no dia mais recente',
        diaAnterior: 'no dia anterior',
        ultimosSeteDias: 'nos últimos 7 dias',
        ultimosTrintaDias: 'nos últimos 30 dias',
        mesMaisRecente: 'no mês mais recente',
        mesAnterior: 'no mês anterior',
        desdeInicio: 'desde o início',
        periodoPersonalizado: this.queryVariables?.measuringDate.length ? `entre ${formattedInitialDate} e ${formattedFinalDate}` : '',
      }

      return periodDescriptions?.[this.dateFilterSelectedPeriod] || ''
    },
    getSelectedCampaignsIds() {
      if (this.stateFilters?.campaigns?.length) return this.stateFilters?.campaigns.map((campaign) => campaign._id)
      return this.selectedCampaignsIds
    },
  },
  async mounted() {
    await this.getData()
    this.$nextTick(() => {
      this.$bus.$on('filter', this.getData)
    })
  },
  destroyed() {
    this.$bus.$off('filter')
  },
  methods: {
    criteriaGroup,
    campaignReceivedCriteria,
    campaignReceivedAndPurchasedCriteria,
    campaignReceivedAndPurchasedAnyProductCriteria,
    campaignReceivedAndOnlyPurchasedAnyProductCriteria,

    async getData() {
      if (this.notExistDateInFilter) return
      await this.getCampaignIds()
      await this.getMetricsKpi()
      await this.getAggregatedCampaignMetrics()
    },

    async getMetricsKpi() {
      const [initialDate = '', finalDate = ''] = assertArray(this.stateFilters.data)

      const variables = {
        initialDate,
        finalDate,
      }

      try {
        const { data } = await this.$apollo.query({
          query: QUERY_METRICS_KPI,
          fetchPolicy: 'no-cache',
          variables,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })

        this.metricsKpi = {
          average_ticket: data?.metricsKpi?.average_ticket?.[0],
          revenue: data?.metricsKpi?.revenue?.[0],
        }
      } catch (error) {
        console.log(error)
      }
    },

    async getCampaignIds() {
      const variables = { ...this.queryVariables, channels: this.channels, necessaryFields: ['_id', 'title'] }
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_GET_CAMPAIGNS_IDS,
          fetchPolicy: 'no-cache',
          variables,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })
        this.selectedCampaignsIds = data?.genericCampaigns.campaigns?.map((campaign) => campaign._id)
      } catch (error) {
        console.log(error)
      }
    },

    async getAggregatedCampaignMetrics() {
      const variables = { ...this.queryVariables, mode: this.mode, campaignIds: this.getSelectedCampaignsIds }
      deepDelete(variables, 'frequencyTime')

      try {
        this.loadingAggregatedCampaignMetrics = true
        const { data } = await this.$apollo.query({
          query: QUERY_GET_AGGREGATED_CAMPAIGNS_RESULTS_METRICS,
          fetchPolicy: 'no-cache',
          variables,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })

        this.aggregatedCampaignMetrics = data?.aggregatedCampaignMetrics
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingAggregatedCampaignMetrics = false
      }
    },

    checkIfSomeCampaignModesHasCosts(mode) {
      const modes = assertArray(mode)
      const hasModesWithCosts = modes.some((mode) => mode === 'SMS' || mode === 'EMAIL')
      const isAllModes = modes.length === 0
      return hasModesWithCosts || isAllModes
    },
    getAverageTicketText(average_ticket_clients_received, average_ticket_total, fractionDigits = 0) {
      if (!average_ticket_clients_received || !average_ticket_total) return ''

      const percentage = average_ticket_clients_received / average_ticket_total
      const percentageDifference = localeString((percentage - 1) * 100, fractionDigits)
      const value = `${percentageDifference}%`
      const description = percentage > 1 ? `a mais do que o das lojas` : `menor que o das lojas`

      return { value, description }
    },
    getRateMetrics(firstNumber, secondNumber, fractionDigits = 0) {
      const rateDivision = firstNumber / secondNumber
      const rateMetrics = rateDivision * 100 || 0

      return `${localeString(rateMetrics, fractionDigits)}%`
    },
  },
}
</script>

<style lang="scss" scoped>
.dash-campanhas-title {
  font-size: 26px;
  color: $cinza-d10 !important;
  font-weight: bold;
}

::v-deep .v-data-table__empty-wrapper {
  color: $cinza-l50 !important;
}
</style>
